<script setup lang="ts">
// The worker avatar, drawn as an SVG group to place inside an <svg>. Faceless, with safety glasses, styled by the
// person's look. Drawn by the designer in Illustrator (iStock 2245303831, reshaped to 2.8 heads) with one arm raised
// on the left to hold a pole; mirror it to put the pole on the right. The artwork is about 260 x 636 units, standing
// with its feet centred on (174, 626).
//
// In the holdingPole pose, the raised arm's parts carry classes so a scene can animate them: worker-arm-sleeve is the
// sleeve, and worker-arm-carried is everything that moves with the hand (glove, cuff stripe and pole).
import { computed } from 'vue';
import { workerAvatarColors } from './worker-avatar';
import type { WorkerAvatarLook } from '../../cross-app-shared/types/worker-avatar';

export type WorkerAvatarPose = 'holdingPole' | 'portrait';

const props = defineProps<{
  look: WorkerAvatarLook;
  // A portrait leaves out the raised arm and pole, for head-and-shoulders crops.
  pose: WorkerAvatarPose;
}>();

const colors = computed(() => workerAvatarColors(props.look));
</script>

<template>
  <g>
    <template v-if="look.hairLength === 'long'">
      <polygon
        points="96 134.6 95.35 139.1 94.7 143.6 94.06 148.1 93.43 152.6 92.83 157.1 92.25 161.6 91.7 166.1 91.18 170.6 90.7 175.1 90.25 179.6 89.86 184.1 89.5 188.6 89.2 193.1 88.95 197.6 88.76 202.1 88.61 206.6 88.53 211.1 88.5 215.6 89.67 220.1 90.83 224.6 92 229.1 93.17 233.6 94.33 238.1 95.5 242.6 96.67 247.1 97.83 251.6 99 256.1 101.84 260.6 104.68 265.1 107.53 269.6 107.53 269.6 108.04 273.48 109.54 277.1 111.92 280.21 115.03 282.59 118.64 284.09 122.53 284.6 225.47 284.6 229.36 284.09 232.97 282.59 236.08 280.21 238.46 277.1 239.96 273.48 240.47 269.6 240.47 269.6 243.32 265.1 246.16 260.6 249 256.1 250.17 251.6 251.33 247.1 252.5 242.6 253.67 238.1 254.83 233.6 256 229.1 257.17 224.6 258.33 220.1 259.5 215.6 259.47 211.1 259.39 206.6 259.24 202.1 259.05 197.6 258.8 193.1 258.5 188.6 258.14 184.1 257.75 179.6 257.3 175.1 256.82 170.6 256.3 166.1 255.75 161.6 255.17 157.1 254.57 152.6 253.94 148.1 253.3 143.6 252.65 139.1 252 134.6 96 134.6"
        :fill="colors.hair"
      />
      <path
        d="M246.92,135.34s-4.77,50.56-7.53,79.67c-1.19,12.64-8.39,24.05-19.53,30.95-8.56,5.29-18.79,11.62-27.38,16.93-10.93,6.77-25.04,6.77-36,0-8.56-5.31-18.82-11.64-27.35-16.93-11.14-6.89-18.34-18.31-19.56-30.95-2.76-29.1-7.53-79.67-7.53-79.67h144.88Z"
        :fill="colors.hairShade"
        fill-rule="evenodd"
      />
    </template>
    <path
      d="M240.87,473.67c.28,6.12-1.19,13.01-2.99,19.31-.29.99-.7,1.94-1.24,2.81-2.16,3.52-7.99,13.05-7.99,13.05,0,0-.9.49-1.97,1.07-.67.36-1.49.32-2.12-.11-.64-.43-.97-1.18-.87-1.93.38-3.02.86-6.89,1.02-8.09.03-.25.05-.5.07-.75.08-1.67.4-8.39.5-10.37.01-.37.01-.74,0-1.1-.11-2.64-.53-13.88-.53-13.88h16.13Z"
      fill="#FFC400"
      fill-rule="evenodd"
    />
    <path
      d="M217.51,296.09l1.16,180.7h23.49s1.71-81.98,1.97-94.41c.01-1.05,0-2.09-.04-3.14-.53-11.56-3.8-83.16-3.8-83.16h-22.79Z"
      :fill="colors.suit"
      fill-rule="evenodd"
    />
    <polygon
      points="218.43 439.18 242.95 439.18 242.46 462.81 218.58 462.81 218.43 439.18"
      :fill="colors.stripe"
      fill-rule="evenodd"
    />
    <path
      d="M218.75,269.08c14.22,3.98,23.31,17.85,21.3,32.48-4.55,33.19-11.93,86.96-11.93,86.96l-2.2,98.44h-103.35l-2.55-98.44s-7.55-53.6-12.24-86.8c-2.06-14.69,7.08-28.64,21.38-32.6,20.12-5.58,45.08-12.49,45.08-12.49,0,0,24.61,6.89,44.5,12.47Z"
      :fill="colors.suit"
      fill-rule="evenodd"
    />
    <polygon
      points="121.24 435.27 120.46 405.15 227.75 405.15 227.07 435.27 121.24 435.27"
      :fill="colors.stripe"
      fill-rule="evenodd"
    />
    <rect x="159.17" y="237.82" width="30.16" height="26.92" :fill="colors.skin" />
    <path
      d="M100.54,137.76s-.29,11.69.21,22.12c.58,12.14,2.78,23.79,5.93,28.97,6.48,10.64,14.53,23.94,14.53,23.94l9-75.04h-29.66Z"
      :fill="colors.hair"
      fill-rule="evenodd"
    />
    <path
      d="M248.44,137.76s.26,11.69-.24,22.12c-.58,12.14-2.75,23.79-5.9,28.97-6.48,10.64-14.53,23.94-14.53,23.94l-9-75.04h29.66Z"
      :fill="colors.hair"
      fill-rule="evenodd"
    />
    <path
      d="M239.16,132.34s-4.26,47.25-6.72,74.45c-1.06,11.81-7.49,22.48-17.44,28.92-7.65,4.94-16.77,10.86-24.45,15.82-9.76,6.33-22.36,6.33-32.15,0-7.65-4.96-16.8-10.88-24.42-15.82-9.95-6.44-16.38-17.11-17.46-28.92-2.46-27.2-6.72-74.45-6.72-74.45h129.35Z"
      :fill="colors.skin"
      fill-rule="evenodd"
    />
    <template v-if="look.hairLength === 'short'">
      <path
        d="M238.1,158.8c3.92-1.3,8.25-.53,11.51,2.06,3.25,2.57,5,6.61,4.63,10.74-.08.71-.13,1.4-.19,2.09-.87,9.63-7.62,17.67-16.93,20.21-4.23,1.14-7.67,2.09-7.67,2.09l3.18-35.37s2.3-.77,5.48-1.83Z"
        :fill="colors.skin"
        fill-rule="evenodd"
      />
      <path
        d="M110.89,158.8c-3.94-1.3-8.28-.53-11.54,2.06-3.25,2.57-4.97,6.61-4.6,10.74.05.71.13,1.4.18,2.09.85,9.63,7.62,17.67,16.93,20.21,4.23,1.14,7.67,2.09,7.67,2.09l-3.17-35.37s-2.33-.77-5.48-1.83Z"
        :fill="colors.skin"
        fill-rule="evenodd"
      />
    </template>
    <path
      d="M106.07,140.33c2.78,13.57,5.24,19.84,11.32,31.94l5.13-31.94h-16.46Z"
      :fill="colors.hair"
      fill-rule="evenodd"
    />
    <path
      d="M242.89,140.33c-2.75,13.57-5.21,19.84-11.32,31.94l-5.13-31.94h16.46Z"
      :fill="colors.hair"
      fill-rule="evenodd"
    />
    <path
      d="M114.7,143.45c1.67,24.66,1.83,44.82-1.14,56.17,12.14-15.66,12.75-31.8,12.01-56.17h-10.87Z"
      fill="#2D475E"
      fill-rule="evenodd"
    />
    <path
      d="M234.29,143.45c-1.69,24.66-1.83,44.82,1.11,56.17-12.12-15.66-12.73-31.8-11.99-56.17h10.87Z"
      fill="#2D475E"
      fill-rule="evenodd"
    />
    <polygon
      points="119.13 163.12 228.87 163.12 233.89 165.63 235.57 172.33 235.57 189.3 230.44 201.64 225.31 210 220.18 209.68 215.05 209.03 209.91 208.06 204.78 206.79 199.65 205.26 194.52 203.49 189.39 201.53 184.26 199.42 179.13 197.21 174 194.95 168.87 197.21 163.74 199.42 158.61 201.53 153.48 203.49 148.35 205.26 143.22 206.79 138.09 208.06 132.95 209.03 127.82 209.68 122.69 210 117.56 201.64 112.43 189.3 112.43 172.33 114.11 165.63 119.13 163.12"
      fill="#C4E2EC"
    />
    <polygon points="135.89 166.47 146.78 166.47 130.86 205 119.97 205 135.89 166.47" fill="#FFF" opacity=".7" />
    <polygon points="151.8 166.47 156.41 166.47 142.59 200.81 137.98 200.81 151.8 166.47" fill="#FFF" opacity=".7" />
    <rect x="107.41" y="158.93" width="133.19" height="7.54" rx="3.77" ry="3.77" fill="#163241" />
    <path
      d="M250.35,132.71H98.64c0-41.91,33.95-75.86,75.86-75.86s75.86,33.95,75.86,75.86Z"
      :fill="colors.hat"
      fill-rule="evenodd"
    />
    <path
      d="M252.52,132.71c1.88,0,3.68.74,5,2.06,1.35,1.32,2.09,3.12,2.09,5v.03c0,1.88-.74,3.68-2.09,5-1.32,1.32-3.12,2.06-5,2.06H96.44c-1.88,0-3.68-.74-5-2.06-1.32-1.32-2.06-3.12-2.06-5v-.03c0-1.88.74-3.68,2.06-5,1.32-1.32,3.12-2.06,5-2.06h156.08Z"
      :fill="colors.hat"
      fill-rule="evenodd"
    />
    <polygon
      points="191.19 132.71 157.77 132.71 163.38 52.99 185.58 52.99 191.19 132.71"
      :fill="colors.hatHighlight"
      fill-rule="evenodd"
    />
    <path
      d="M138.3,600.78s-18.97,7.82-29,11.96c-3.66,1.51-6.03,5.08-6.01,9.04.03,2.4.04,4.54.04,4.54h68.26v-25.53h-33.3Z"
      fill="#92A5B9"
      fill-rule="evenodd"
    />
    <path
      d="M210.11,600.78s18.97,7.82,29,11.96c3.65,1.51,6.03,5.08,5.99,9.04-.01,2.4-.03,4.54-.03,4.54h-68.26v-25.53h33.3Z"
      fill="#92A5B9"
      fill-rule="evenodd"
    />
    <polygon
      points="173.39 466.05 172.73 600.38 135.86 600.38 122.57 477.22 122.03 466.05 173.39 466.05"
      :fill="colors.suit"
      fill-rule="evenodd"
    />
    <polygon
      points="175.03 466.05 175.68 600.38 212.56 600.38 225.92 477.22 226.38 466.05 175.03 466.05"
      :fill="colors.suit"
      fill-rule="evenodd"
    />
    <rect x="186.78" y="325.65" width="33.75" height="30.51" :fill="colors.suitShade" />
    <rect x="127.3" y="325.65" width="33.75" height="30.51" :fill="colors.suitShade" />
    <rect x="186.78" y="312.74" width="33.75" height="9.63" :fill="colors.suitShade" />
    <rect x="127.3" y="312.74" width="33.75" height="9.63" :fill="colors.suitShade" />
    <path
      d="M172.69,268.64c0-.86.7-1.56,1.56-1.56s1.56.7,1.56,1.56v180.29c0,.86-.7,1.56-1.56,1.56s-1.56-.7-1.56-1.56v-180.29Z"
      fill="#92A5B9"
      fill-rule="evenodd"
    />
    <polygon points="174.25 277.87 162.05 263.79 186.44 263.79 174.25 277.87" fill="#163241" fill-rule="evenodd" />
    <polygon
      points="150.2 263.27 158.28 254.02 174.25 277.87 154.35 273.63 156.78 266.96 150.2 263.27"
      :fill="colors.suitShade"
      fill-rule="evenodd"
    />
    <polygon
      points="198.28 263.27 190.21 254.02 174.25 277.87 194.13 273.63 191.71 266.96 198.28 263.27"
      :fill="colors.suitShade"
      fill-rule="evenodd"
    />
    <path
      d="M133.82,587.4l-1.43-24.91h40.49l-.07,24.91h-39ZM175.53,562.48h40.49l-1.44,24.91h-39l-.05-24.91Z"
      :fill="colors.stripe"
      fill-rule="evenodd"
    />
    <template v-if="pose === 'holdingPole'">
      <path
        class="worker-arm-carried"
        d="M21.01,93.36c-1.65,1.25-2.52,3.29-2.27,5.33.8,6.61,2.5,20.78,2.5,20.78l12.59.35s-5.28-17.35-7.72-25.39c-.28-.91-.99-1.64-1.9-1.93-.91-.29-1.91-.13-2.66.45-.18.13-.36.28-.54.4Z"
        fill="#FFC400"
        fill-rule="evenodd"
      />
      <polygon
        class="worker-arm-carried"
        points="7.01 131.47 26.18 150.74 42.13 140.52 33.83 119.82 18.1 100.9 7.01 131.47"
        fill="#FFC400"
        fill-rule="evenodd"
      />
      <path
        class="worker-arm-sleeve"
        d="M143.55,267.62l-25.7,55.8c-20.75-24.02-43.29-45.24-66.68-73.01-2.19-2.6-5.25-7.58-6.38-11.36-9.56-32.19-18.47-63.68-26.15-90.1l25.92-10.41,36.69,85.37,62.3,43.71Z"
        :fill="colors.suit"
        fill-rule="evenodd"
      />
      <path
        class="worker-arm-carried"
        d="M29.5,186.58c-2.26-7.83-4.45-15.45-6.57-22.78l27.92-10.62,9.34,21.72-30.69,11.67Z"
        :fill="colors.stripe"
        fill-rule="evenodd"
      />
      <rect
        class="worker-arm-carried"
        x="18.45"
        y="2.19"
        width="8.96"
        height="633.81"
        rx="4.48"
        ry="4.48"
        fill="#E2E7E8"
      />
      <path
        class="worker-arm-carried"
        d="M3.14,103.6c-.88,2.15-1.12,4.49-.72,6.77.84,4.76,2.34,13.27,3.28,18.58.46,2.59,2.94,4.32,5.53,3.86,1.14-.2,2.34-.41,3.45-.61,2.64-.46,4.79-2.38,5.56-4.94,1.43-4.74,3.81-12.58,5.63-18.64.67-2.16.35-4.52-.85-6.44-1.21-1.92-3.18-3.23-5.43-3.59-2.93-.47-6.1-.98-8.62-1.39-2.88-.46-5.67,1.11-6.77,3.8-.37.88-.73,1.78-1.07,2.6Z"
        fill="#FFC400"
        fill-rule="evenodd"
      />
    </template>
  </g>
</template>
